// Adapter operator-work (backend) → OperatorWork (modelo da UI).
// O endpoint entrega linhas POR TICKET (OperatorTicketDto), sem os tickets irmãos do
// agregador — então montamos um agregador SINTÉTICO de 1 ticket. A verdade de
// elegibilidade/bloqueio vem do servidor (campos do JourneyTicketDto via adapt.ticket).
import type { Aggregator, Attendant, EligibilityInfo, OperatorRow, OperatorWork } from '../../domain/types'
import { LOCATION } from '../../domain/catalog'
import type { OperatorWorkDto, OperatorTicketDto } from '../dto/journey.dto'
import type { AttendantDto } from '../dto/reference.dto'
import { ticket as adaptTicket, customer, metadata, hhmm, priority, friendlyCode } from './journey.adapter'

const isNumericId = (id: unknown): boolean => typeof id === 'number' || (typeof id === 'string' && /^\d+$/.test(id))

function attendant(d: AttendantDto): Attendant | null {
  const candidates = [d.resourceId, d.ResourceId, d.id, d.Id, d.userId, d.UserId]
  const id = candidates.find(isNumericId) ?? candidates.find((value) => value != null && value !== '')
  if (id == null || id === '') {return null}
  return {
    id: String(id),
    nome: d.name ?? d.Name ?? d.resourceName ?? d.ResourceName ?? d.fullName ?? d.FullName ?? d.displayName ?? d.DisplayName ?? 'Atendente',
  }
}

function row(d: OperatorTicketDto): OperatorRow {
  const t = adaptTicket(d.ticket)
  const aggregatorId =
    (d as unknown as { aggregatorId?: string }).aggregatorId ??
    (d as unknown as { AggregatorId?: string }).AggregatorId ??
    (d.ticket as unknown as { aggregatorId?: string })?.aggregatorId ??
    (d.ticket as unknown as { AggregatorId?: string })?.AggregatorId ??
    ''
  const a: Aggregator = {
    aggregatorId,
    // operator-work no backend só retorna jornadas confirmadas (InProgress), então o
    // agregador sintético nasce InProgress. Sem isso, row.a.status ficaria undefined e o
    // filtro por status dependeria 100% da lista de journeys (escondendo linhas ausentes).
    status: 'InProgress',
    criadoEm: hhmm(d.createdAt) ?? '—',
    cliente: customer(d.customer),
    localizacao: LOCATION,
    // mesma normalização do adapter de journeys — o backend pode mandar a prioridade como
    // string (ex.: "Preferencial", "80+") em vez do número que o tipo promete.
    prioridade: priority(d.priority),
    tipoAtendimento: 'PRESENTIAL',
    metadata: metadata(d.metadata),
    tickets: [t], // sintético: só o próprio ticket
    _createdAtMs: d.createdAt ? Date.parse(d.createdAt) : Date.now(),
  }
  const info: EligibilityInfo = {
    podeIniciar: t.podeIniciar ?? true,
    motivo: t.motivoBloqueio ?? null,
    bloqueadoPor: t.bloqueadoPor ?? [],
    razao: t.razao ?? null,
  }
  const depNames = (d.ticket.blockedBy ?? [])
    .map((item) => ({ code: friendlyCode(item.friendlyCode), nome: item.serviceName ?? '' }))
    .filter((item) => item.code !== '—' && item.nome)
  return { t, a, info, depNames }
}

export function operatorWork(dto: OperatorWorkDto): OperatorWork {
  return {
    serving: (dto.serving ?? []).map(row),
    eligible: (dto.eligible ?? []).map(row),
    blocked: (dto.blocked ?? []).map(row),
    attendants: (dto.attendants ?? []).map(attendant).filter((item): item is Attendant => item != null),
    eligCountByServiceConfig: {}, // preenchido no RealDataSource via waitingByServiceConfig()
    eligCountBySession: dto.eligCountBySession ?? {},
  }
}
